import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { PrimaryButton } from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import Table from '../../components/Table'
import AutomationModal from './AutomationModal'
import Radom, { Automation } from '../../state/Radom'
import TimeText from '../../components/TimeText'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Dots from '../../icons/Dots'
import { cutString, successToast } from '../../util/Util'
import { startCase, uniq } from 'lodash'
import { Container } from '../../components/Animations'
import {
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceHeader,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import { getMethod } from '../../util/Managed'
import { formatDistanceToNow } from 'date-fns'

const getAutomationAssetsLabel = (automation: Automation): string => {
  const assets = automation.automationDetails.Withdrawal?.assets ?? []
  if (assets.length <= 0) {
    return 'No assets selected'
  }

  return uniq(assets.map(asset => getMethod(asset.network, asset.token).ticker)).join(', ')
}

const getAutomationTriggerLabel = (automation: Automation): string => {
  const triggers = automation.automationDetails.Withdrawal?.triggers ?? []
  const minimumAmount = triggers.find(t => t.minimumAmount)?.minimumAmount
  const timeInterval = triggers.find(t => t.timeInterval)?.timeInterval
  const withdrawalAmount = triggers.find(t => t.withdrawalAmount)?.withdrawalAmount

  if (withdrawalAmount && timeInterval) {
    return `${withdrawalAmount.amount} ${withdrawalAmount.currency} every ${timeInterval.interval}`
  }

  if (minimumAmount) {
    return `Balance above ${minimumAmount.amount} ${minimumAmount.currency}`
  }

  if (timeInterval) {
    return `Every ${startCase(timeInterval.interval)}`
  }

  return 'No trigger configured'
}

const AutomationView = observer((): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [automations, setAutomations] = useState<Automation[]>([])

  const listAutomations = (): void => {
    setIsLoading(true)
    setAutomations([])
    Radom.listAutomations()
      .then(a => setAutomations(a))
      .catch(err => {
        console.error('Failed to load automations', err)
        setAutomations([])
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    listAutomations()
  }, [])

  const withdrawalAutomationCount = automations.filter(a => a.automationType === 'withdrawal').length
  const destinationAccountCount = uniq(automations
    .map(a => a.automationDetails.Withdrawal?.withdrawalAccountId)
    .filter(Boolean)).length
  const watchedAssetCount = uniq(automations.flatMap(a =>
    (a.automationDetails.Withdrawal?.assets ?? []).map(asset => `${asset.network}:${asset.token ?? ''}`)
  )).length
  const lastTriggeredAutomation = automations
    .filter(a => a.lastTriggeredAt)
    .sort((a, b) => new Date(b.lastTriggeredAt).getTime() - new Date(a.lastTriggeredAt).getTime())[0]

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader
          title='Automations' />
        <PageActionRow>
          <PrimaryButton style={{ gap: 8 }} onClick={() => setIsOpen(true)}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
            <span>Create automation</span>
          </PrimaryButton>
        </PageActionRow>
      </PageHeaderRow>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>Automation readiness</SurfaceTitle>
          </div>
          <InlinePill $tone={automations.length > 0 ? 'green' : 'neutral'}>
            {automations.length > 0 ? 'Configured' : 'No rules'}
          </InlinePill>
        </SurfaceHeader>
        <SummaryStrip>
          <SummaryStripItem>
            <SummaryLabel>Configured rules</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : automations.length}
            </SummaryValue>
            <SummaryMeta>{withdrawalAutomationCount} withdrawal workflow{withdrawalAutomationCount === 1 ? '' : 's'}</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Destination accounts</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : destinationAccountCount}
            </SummaryValue>
            <SummaryMeta>Saved accounts used by rules</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Watched assets</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : watchedAssetCount}
            </SummaryValue>
            <SummaryMeta>Assets included in rules</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Last run</SummaryLabel>
            <SummaryValue style={{ fontSize: 18 }}>
              {isLoading
                ? <LoadingBar style={{ height: 24, width: 92 }} />
                : lastTriggeredAutomation
                  ? formatDistanceToNow(new Date(lastTriggeredAutomation.lastTriggeredAt), { addSuffix: true })
                  : 'Not run yet'}
            </SummaryValue>
            <SummaryMeta>Most recent automation trigger</SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>Rules</SurfaceTitle>
          </div>
        </SurfaceHeader>

        <Table
          headers={['Name', 'Type', 'Trigger', 'Assets', 'Destination', 'Last run', 'Created', ''].map(t => <span key={t}>{t}</span>)}
          tableContainerStyle={{
            gridTemplateColumns: 'minmax(180px, 1fr) minmax(120px, 0.6fr) minmax(190px, 1fr) minmax(140px, 0.8fr) minmax(160px, 0.9fr) minmax(130px, 0.7fr) minmax(130px, 0.7fr) minmax(54px, auto)'
          }}
          items={automations.map(a => [
            <div key={`${a.id}-name`} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span>{a.name}</span>
              <span style={{ opacity: 0.5, fontSize: 13 }}>{cutString(a.id, 8)}</span>
            </div>,
            <InlinePill key={`${a.id}-type`} $tone='blue'>
              {startCase(a.automationType)}
            </InlinePill>,
            <span key={`${a.id}-trigger`}>{getAutomationTriggerLabel(a)}</span>,
            <span key={`${a.id}-assets`}>{getAutomationAssetsLabel(a)}</span>,
            <span key={`${a.id}-destination`}>
              {a.automationDetails.Withdrawal?.withdrawalAccountId
                ? cutString(a.automationDetails.Withdrawal.withdrawalAccountId, 8)
                : 'No destination'}
            </span>,
            <div key={`${a.id}-last-run`}>
              {a.lastTriggeredAt ? <TimeText date={new Date(a.lastTriggeredAt)} /> : <span style={{ color: '#7d8492' }}>Not run yet</span>}
            </div>,
            <div key={`${a.id}-created`}>
              <TimeText key="txTime" date={new Date(a.createdAt as any)} />
            </div>,
            <div key={a.id} onClick={e => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>

              <Dropdown
                noChevron
                selectedContent={<Dots width={15} />}
                dropdownContent={
                  <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                    <ConfirmButtonModal
                      title='Delete automation'
                      description='Are you sure you want to delete this automation?'
                      action='Delete automation'
                      onConfirm={async () => {
                        Radom.deleteAutomation(a.id)
                          .then(async () => listAutomations())
                          .then(() => successToast('Automation deleted'))
                      }}
                      ActionComponent={props => <DropdownItem {...props}>Delete automation</DropdownItem>}
                    />
                  </div>
                }
              />
            </div>

          ]
          )}
          isLoading={isLoading}
          emptyPlaceholderText='No automations yet. Create a rule to automatically move balances to a saved withdrawal account.'
        />
      </Surface>

      {
        isOpen &&
      <AutomationModal
        onAutomationCreated={() => listAutomations()}
        visible={isOpen}
        onClose={() => setIsOpen(false)} />
      }
    </DashboardPage>
  </Container>
})

export default AutomationView
